import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../Contexts/authContext';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './UserProfile.module.css';

const UserProfile = ({ customTrigger }) => {
    const { user, logout } = useAuth();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);

    // Get user initials
    const getInitials = (firstName, lastName) => {
        if (!firstName) return '?';
        const firstInitial = firstName.charAt(0).toUpperCase();
        const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
        return firstInitial + lastInitial;
    };

    // Mapping of stored sport values to translation keys
    const getSportTranslation = (sport) => {
        if (!sport) return '';
        const lowerSport = sport.toLowerCase();
        const mapping = {
            'futebol': 'football',
            'voleibol': 'volleyball',
            'andebol': 'handball',
            'basquetebol': 'basketball',
            'futsal': 'futsal'
        };
        const key = mapping[lowerSport] || lowerSport;
        return t(`sports.${key}`);
    };

    // Format date to readable string
    const formatDate = (date) => {
        if (!date) return '';
        // Use current language for date formatting
        const locale = i18n.language === 'pt' ? 'pt-PT' : i18n.language;
        return date.toLocaleDateString(locale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Close modal when clicking outside
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const handleLogout = () => {
        const confirmLogout = window.confirm(t('header.logoutConfirm'));
        if (confirmLogout) {
            setIsOpen(false);
            logout();
            navigate(`/${i18n.language}`);
        }
    };

    // Calculate subscription expiration date (1 year from start)
    const getSubscriptionDates = () => {
        const isActive = user?.subscriptionStatus === 'active';

        if (!user?.subscriptionDate) {
            return {
                start: null,
                end: null,
                isActive: isActive
            };
        }

        const startDate = new Date(user.subscriptionDate);
        const endDate = new Date(startDate);
        endDate.setFullYear(endDate.getFullYear() + 1);

        return {
            start: startDate,
            end: endDate,
            isActive: isActive // Trust the status from backend
        };
    };

    const subscriptionInfo = getSubscriptionDates();

    const modalContent = (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        className={styles.backdrop}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsOpen(false)}
                    />

                    {/* Modal Content */}
                    <motion.div
                        className={styles.modal}
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        transition={{ type: "spring", duration: 0.3 }}
                    >
                        {/* User Info */}
                        <div className={styles.userInfo}>
                            <h2 className={styles.userName}>
                                {user?.firstName} {user?.lastName}
                            </h2>
                            <p className={styles.userEmail}>{user?.email}</p>
                        </div>

                        {/* Details Section */}
                        <div className={styles.details}>
                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>
                                    <i className="fas fa-running"></i>
                                    {t('userProfile.sport')}
                                </span>
                                <span className={styles.detailValue}>
                                    {getSportTranslation(user?.selectedSport)}
                                </span>
                            </div>

                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>
                                    <i className="fas fa-calendar-alt"></i>
                                    {t('userProfile.registeredSince')}
                                </span>
                                <span className={styles.detailValue}>
                                    {user?.createdAt ? formatDate(new Date(user.createdAt)) : t('common.n_a')}
                                </span>
                            </div>
                        </div>

                        {/* Subscription Status */}
                        <div className={styles.subscriptionSection}>
                            <h3 className={styles.sectionTitle}>
                                {t('userProfile.subscriptionStatus')}
                            </h3>

                            {user?.role === 'admin' ? (
                                /* Admin users - unlimited subscription */
                                <div className={styles.subscriptionActive}>
                                    <div className={styles.statusBadge}>
                                        <i className="fas fa-shield-alt"></i>
                                        {t('userProfile.adminAccount')}
                                    </div>
                                    <div className={styles.subscriptionDates}>
                                        <div className={styles.dateRow}>
                                            <span className={styles.dateLabel}>
                                                <i className="fas fa-infinity" style={{ marginRight: '6px' }}></i>
                                                {t('userProfile.unlimitedSubscription')}
                                            </span>
                                        </div>
                                        <div className={styles.dateRow}>
                                            <span className={styles.dateLabel}>
                                                <i className="fas fa-futbol" style={{ marginRight: '6px' }}></i>
                                                {t('userProfile.allSportsAccess')}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ) : subscriptionInfo?.isActive ? (
                                /* Active paid subscribers */
                                <div className={styles.subscriptionActive}>
                                    <div className={styles.statusBadge}>
                                        <i className="fas fa-check-circle"></i>
                                        {t('userProfile.activeSubscriber')}
                                    </div>
                                    <div className={styles.subscriptionDates}>
                                        <div className={styles.dateRow}>
                                            <span className={styles.dateLabel}>
                                                {t('userProfile.activeSince')}:
                                            </span>
                                            <span className={styles.dateValue}>
                                                {subscriptionInfo.start ? formatDate(subscriptionInfo.start) : formatDate(new Date(user?.createdAt))}
                                            </span>
                                        </div>
                                        <div className={styles.dateRow}>
                                            <span className={styles.dateLabel}>
                                                {t('userProfile.activeUntil')}:
                                            </span>
                                            <span className={styles.dateValue}>
                                                {subscriptionInfo.end ? formatDate(subscriptionInfo.end) : t('common.n_a')}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* Inactive/no subscription */
                                <div className={styles.subscriptionInactive}>
                                    <div className={styles.statusBadge}>
                                        <i className="fas fa-times-circle"></i>
                                        {t('userProfile.inactiveSubscriber')}
                                    </div>
                                    <p className={styles.inactiveText}>
                                        {t('userProfile.subscribeMessage')}
                                    </p>
                                    <Link
                                        to={`/${i18n.language}/pricing`}
                                        className={styles.subscribeButton}
                                        onClick={() => setIsOpen(false)}
                                    >
                                        <i className="fas fa-crown"></i>
                                        {t('userProfile.subscribeNow')}
                                    </Link>
                                </div>
                            )}
                        </div>

                        {/* Logout Button */}
                        <button className={styles.logoutButton} onClick={handleLogout}>
                            <i className="fas fa-sign-out-alt"></i>
                            {t('header.logout')}
                        </button>

                        {/* Close Button */}
                        <button
                            className={styles.closeButton}
                            onClick={() => setIsOpen(false)}
                        >
                            <i className="fas fa-times"></i>
                            {t('common.close')}
                        </button>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );

    return (
        <>
            {/* Trigger Button */}
            {customTrigger ? (
                <div onClick={() => setIsOpen(true)} style={{ display: 'inline-block', cursor: 'pointer' }}>
                    {customTrigger}
                </div>
            ) : (
                <button
                    className={styles.trigger}
                    onClick={() => setIsOpen(true)}
                    aria-label={t('userProfile.openProfile')}
                >
                    <span className={styles.welcomeText}>
                        {t('header.hello')}, {user?.firstName || t('userProfile.user')}
                    </span>
                </button>
            )}

            {ReactDOM.createPortal(modalContent, document.body)}
        </>
    );
};

export default UserProfile;
